import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Paperclip, PlayCircle, File, Edit3, Eye, ChevronRight } from 'react-feather';
import Modal, { ModalHandle } from './Modal';
import { integrationsAPI, gmailSendErrorMessage } from '../services/integrationsAPI';
import IntegrationHealthBanner from './IntegrationHealthBanner';
import { useAuth } from '../contexts/AuthContext';
import { userAPI, familyAPI, athleteAPI, athleteSharingAPI } from '../services/api';
import { mediaUploadService } from '../services/mediaUploadService';
import { useToast } from '../contexts/ToastContext';
import YouTubeVideoPickerModal from './YouTubeVideoPickerModal';
import TemplatePickerModal from './TemplatePickerModal';
import { useEmailVariables } from '../hooks/useEmailVariables';
import { QuickTags } from './QuickTags';
import { QuickTag, coachEmailTemplateBody } from '../utils/emailVariables';
import { useResponsive } from '../hooks/useResponsive';
import { TextInput, TextArea } from './ui';

interface Coach {
  name: string;
  email: string;
  title?: string;
  school?: string;
  coach_id?: string;
  school_id?: string;
}

interface EmailCoachModalProps {
  isOpen: boolean;
  onClose: () => void;
  coach: Coach;
  /** Render inside the unified CoachContactModal shell (no own overlay). */
  embedded?: boolean;
}

interface MediaFile {
  file: File;
  preview: string;
  type: 'image' | 'video' | 'document';
}

interface AcademicData {
  satMath?: string;
  satReadingWriting?: string;
  satTotal?: string;
  psatMath?: string;
  psatReadingWriting?: string;
  psatTotal?: string;
  actTotal?: string;
  gpaScale?: string;
  currentGpa?: string;
}

interface PerformanceMetrics {
  sixty_yard_time?: number | null;
  forty_yard_time?: number | null;
  twenty_yard_time?: number | null;
  ten_yard_time?: number | null;
  home_to_first?: number | null;
  pop_time?: number | null;
  exit_velocity?: number | null;
  overhand_velocity?: number | null;
  grip_strength?: number | null;
}

interface SportInfo {
  sport: string;
  positions: string[];
  bats: string;
  throws: string;
  height: string;
  weight: number;
  awards: string[];
}

interface AthleteProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  graduationYear: number;
  city: string;
  state: string;
  sport?: string;
  grade?: number;
  position?: string;
  createdBy: string;
  createdAt: string;
  youtube_channel_url?: string;
  youtube_channel_handle?: string;
  /** APS-259. The highlight video link (YouTube). */
  featured_video_url?: string | null;
}

const EmailCoachModal: React.FC<EmailCoachModalProps> = ({ isOpen, onClose, coach, embedded = false }) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [athleteId, setAthleteId] = useState<string | null>(null);
  const [athleteProfile, setAthleteProfile] = useState<AthleteProfile | null>(null);
  const [sportInfo, setSportInfo] = useState<SportInfo | null>(null);
  const [academicData, setAcademicData] = useState<AcademicData | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<{ school: { name: string }, travelClub: { name: string } } | null>(null);
  // Public profile share link — only present when the athlete has sharing
  // enabled. Drives the {{athlete.profileShareLink}} tag (offered only when
  // set) and the "enable sharing" hint below the editor.
  const [profileShareUrl, setProfileShareUrl] = useState<string | undefined>(undefined);
  // APS-259. The template seeds only after the share-link fetch has settled,
  // so the "My APS profile" line goes in when the link exists.
  const [shareSettled, setShareSettled] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachedMedia, setAttachedMedia] = useState<MediaFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [cursorPosition, setCursorPosition] = useState(0);
  // P2.5: pre-flight is silent-strip now (see handleSendEmail). No blocking
  // state needed; missing-tag analytics + the optional passive-signal hint
  // below the editor are the entire UX.
  const [youtubePickerVisible, setYoutubePickerVisible] = useState(false);
  const [templatePickerVisible, setTemplatePickerVisible] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const emailBodyRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // One-shot guard so the auto-template only seeds once per open — a manual clear
  // of both fields must not re-inject the boilerplate. Reset in handleClose.
  const hasSeededTemplateRef = useRef(false);

  // Use shared email variables system
  const { tags, resolveVariables } = useEmailVariables({
    athlete: athleteProfile,
    coach,
    sportInfo,
    academicData,
    performanceMetrics,
    schoolInfo,
    profileShareUrl,
    context: {
      hasCoach: true,
      hasYouTube: !!athleteProfile?.youtube_channel_url,
      isThreadReply: false
    }
  });

  // Load athlete data when modal opens
  useEffect(() => {
    if (isOpen && sessionToken) {
      // Clear stale data first, then load fresh data
      setAthleteProfile(null);
      setAthleteId('');
      setSchoolInfo(null);
      setSportInfo(null);
      setAcademicData(null);
      setPerformanceMetrics(null);
      setIsCheckingConnection(true);
      setProfileShareUrl(undefined);
      setShareSettled(false);
      loadAthleteProfile();
    } else if (!isOpen) {
      // Clear cached athlete data when modal closes to prevent stale data
      setAthleteProfile(null);
      setAthleteId('');
      setSchoolInfo(null);
      setSportInfo(null);
      setAcademicData(null);
      setPerformanceMetrics(null);
      setProfileShareUrl(undefined);
    }
  }, [isOpen, sessionToken]);

  // Fetch the athlete's public profile share URL. Only resolves to a value
  // when sharing is enabled (share_enabled + custom_slug); otherwise stays
  // undefined so the Profile Share Link tag isn't offered and the hint shows.
  const loadProfileShareUrl = async (athleteProfileId: string) => {
    if (!sessionToken) return;
    try {
      const res = await athleteSharingAPI.getShareSettings(sessionToken, athleteProfileId);
      if (res?.data?.shareUrl) setProfileShareUrl(res.data.shareUrl);
    } catch {
      /* sharing not enabled or fetch failed — tag stays unavailable */
    } finally {
      setShareSettled(true);
    }
  };

  // APS-259. The highlight video link comes from my-athletes, which carries
  // featured_video_url. null when there is none or the lookup fails.
  const loadFeaturedVideoUrl = async (athleteProfileId: string): Promise<string | null> => {
    if (!sessionToken) return null;
    try {
      const res = await familyAPI.getMyAthletes(sessionToken);
      return res.athletes?.find((a) => a.id === athleteProfileId)?.featured_video_url || null;
    } catch {
      return null;
    }
  };

  // Check Gmail connection when athlete ID is available
  useEffect(() => {
    if (athleteId && isCheckingConnection) {
      const initializeModal = async () => {
        await checkGmailConnection();
        setIsCheckingConnection(false);
      };
      initializeModal();
    }
  }, [athleteId]);

  // Generate template after connection check completes and Gmail is connected.
  // One-shot (hasSeededTemplateRef): once seeded, a manual select-all-delete of
  // subject+body must NOT re-inject the boilerplate — so subject/emailBody are
  // deliberately excluded from the deps.
  useEffect(() => {
    if (!hasSeededTemplateRef.current && !isCheckingConnection && isGmailConnected && athleteProfile && shareSettled && !subject && !emailBody) {
      hasSeededTemplateRef.current = true;
      generateEmailTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckingConnection, isGmailConnected, athleteProfile, shareSettled]);

  const loadAthleteProfile = async () => {
    if (!sessionToken) return;

    try {
      const userProfile = await userAPI.getProfile(sessionToken);
      const familyResult = await familyAPI.getMyFamily(sessionToken);

      if (familyResult.family?.members) {
        const familyMember = familyResult.family.members.find(
          (member: any) => member.id === userProfile.id
        );

        if (familyMember?.athlete_profile_id) {
          setAthleteId(familyMember.athlete_profile_id);
          loadProfileShareUrl(familyMember.athlete_profile_id);
          await loadAthleteDataWithId(familyMember.athlete_profile_id);
        } else {
          await loadAthleteDataFallback();
        }
      } else {
        await loadAthleteDataFallback();
      }
    } catch (error) {
      console.error('Failed to load family data:', error);
      await loadAthleteDataFallback();
    }
  };

  const loadAthleteDataWithId = async (athleteProfileId: string) => {
    if (!sessionToken) return;

    try {
      const [profileData, videoUrl] = await Promise.all([
        userAPI.getComprehensiveProfile(sessionToken),
        loadFeaturedVideoUrl(athleteProfileId),
      ]);

      if (profileData.athletes && profileData.athletes.length > 0) {
        const athlete = profileData.athletes.find((a: any) => a.id === athleteProfileId);

        if (athlete) {
          const transformedProfile = {
            id: athlete.id,
            firstName: athlete.firstName,
            lastName: athlete.lastName,
            email: athlete.email,
            phone: athlete.phone,
            dateOfBirth: athlete.dateOfBirth,
            graduationYear: athlete.graduationYear,
            city: athlete.city,
            state: athlete.state,
            sport: athlete.sport,
            grade: parseInt(athlete.grade) || undefined,
            position: athlete.position,
            createdBy: athlete.createdBy,
            createdAt: athlete.createdAt,
            youtube_channel_url: (athlete as any).youtube_channel_url,
            youtube_channel_handle: (athlete as any).youtube_channel_handle,
            featured_video_url: (athlete as any).featuredVideoUrl || (athlete as any).featured_video_url || videoUrl,
          };

          setAthleteProfile(transformedProfile);

          if ((athlete as any).schoolInfo) {
            setSchoolInfo((athlete as any).schoolInfo);
          }

          if (athlete.sportInfo) {
            const transformedSportInfo = {
              sport: athlete.sportInfo.sport,
              positions: typeof athlete.sportInfo.positions === 'string'
                ? athlete.sportInfo.positions.split(', ').filter((p: string) => p.trim())
                : Array.isArray(athlete.sportInfo.positions)
                  ? athlete.sportInfo.positions
                  : [],
              bats: athlete.sportInfo.bats,
              throws: athlete.sportInfo.throws,
              height: typeof athlete.sportInfo.height === 'number'
                ? athlete.sportInfo.height.toString()
                : athlete.sportInfo.height,
              weight: athlete.sportInfo.weight,
              awards: Array.isArray(athlete.sportInfo.awards)
                ? athlete.sportInfo.awards
                : []
            };
            setSportInfo(transformedSportInfo);
          }

          // Load academic data
          try {
            const academicResult = await athleteAPI.getAcademicPerformance(sessionToken, athleteProfileId);
            if (academicResult.success && academicResult.data?.academicPerformance) {
              setAcademicData(academicResult.data.academicPerformance);
            }
          } catch (error) {
            console.log('No academic data available');
          }

          // Load performance metrics
          try {
            const performanceResult = await athleteAPI.getPerformanceMetrics(sessionToken, athleteProfileId);
            if (performanceResult.success && performanceResult.data?.metrics) {
              setPerformanceMetrics(performanceResult.data.metrics);
            }
          } catch (error) {
            console.log('No performance metrics available');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load comprehensive profile:', error);
      await loadAthleteDataFallback();
    }
  };

  const loadAthleteDataFallback = async () => {
    if (!sessionToken) return;

    try {
      const athleteResult = await athleteAPI.getMyProfile(sessionToken);
      
      if (athleteResult.profile) {
        const currentAthleteId = athleteResult.profile.id;
        setAthleteId(currentAthleteId);
        // #200: the fallback path also needs the share URL, otherwise the
        // {{athlete.profileShareLink}} tag is silently unavailable here.
        loadProfileShareUrl(currentAthleteId);
        const videoUrl = await loadFeaturedVideoUrl(currentAthleteId);
        setAthleteProfile({ ...(athleteResult.profile as any), featured_video_url: videoUrl });

        try {
          const sportInfoResult = await athleteAPI.getSportInfo(sessionToken, currentAthleteId);
          if (sportInfoResult.sportInfo) {
            setSportInfo(sportInfoResult.sportInfo);
          }
        } catch (error) {
          console.warn('No sport info available');
        }

        try {
          const academicResult = await athleteAPI.getAcademicPerformance(sessionToken, currentAthleteId);
          if (academicResult.success && academicResult.data?.academicPerformance) {
            setAcademicData(academicResult.data.academicPerformance);
          }
        } catch (error) {
          console.log('No academic data available');
        }

        try {
          const performanceResult = await athleteAPI.getPerformanceMetrics(sessionToken, currentAthleteId);
          if (performanceResult.success && performanceResult.data?.metrics) {
            setPerformanceMetrics(performanceResult.data.metrics);
          }
        } catch (error) {
          console.log('No performance metrics available');
        }
      }
    } catch (error) {
      console.error('Failed to load fallback data:', error);
    }
  };

  const checkGmailConnection = async () => {
    if (!athleteId) {
      setIsGmailConnected(false);
      return;
    }

    try {
      const connectionStatus = await integrationsAPI.getConnectionStatus(athleteId);
      setIsGmailConnected(connectionStatus.gmail);
    } catch (error) {
      console.error('Error checking Gmail connection:', error);
      setIsGmailConnected(false);
    }
  };

  const generateEmailTemplate = () => {
    if (!subject && !emailBody && athleteProfile) {
      setSubject('Ref: {{athlete.name}} {{athlete.graduationYear}} {{athlete.position}}');
      setEmailBody(coachEmailTemplateBody({
        hasVideo: !!athleteProfile.featured_video_url,
        hasProfileLink: !!profileShareUrl,
      }));
    }
  };

  const insertQuickTag = (tag: QuickTag) => {
    const currentText = emailBody;
    const beforeCursor = currentText.substring(0, cursorPosition);
    const afterCursor = currentText.substring(cursorPosition);
    const newText = beforeCursor + tag.value + afterCursor;
    const newCursorPosition = cursorPosition + tag.value.length;

    setEmailBody(newText);

    setTimeout(() => {
      if (emailBodyRef.current) {
        emailBodyRef.current.focus();
        emailBodyRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
      setCursorPosition(newCursorPosition);
    }, 10);
  };

  const handleYouTubeVideosSelected = (videoLinks: string[]) => {
    const currentBody = emailBody;
    const videosText = videoLinks.join('\n');
    const newBody = currentBody ? `${currentBody}\n\n${videosText}` : videosText;
    setEmailBody(newBody);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: MediaFile[] = [];

    for (let i = 0; i < Math.min(files.length, 4 - attachedMedia.length); i++) {
      const file = files[i];

      // Check file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.warning(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      let type: 'image' | 'video' | 'document' = 'document';
      if (file.type.startsWith('image/')) type = 'image';
      else if (file.type.startsWith('video/')) type = 'video';

      const preview = URL.createObjectURL(file);

      newFiles.push({ file, preview, type });
    }

    if (newFiles.length > 0) {
      setAttachedMedia([...attachedMedia, ...newFiles]);
    }

    // Reset input
    e.target.value = '';
  };

  const removeMediaFile = (index: number) => {
    URL.revokeObjectURL(attachedMedia[index].preview);
    setAttachedMedia(attachedMedia.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const updated = { ...prev };
      delete updated[`media_${index}`];
      return updated;
    });
  };

  const uploadMediaFiles = async (): Promise<string[]> => {
    if (attachedMedia.length === 0) return [];

    if (!athleteId || !sessionToken) {
      throw new Error('Missing athlete ID or session token');
    }

    try {
      const uploadResults = await mediaUploadService.uploadMultipleMedia(
        attachedMedia,
        athleteId,
        sessionToken,
        (index, progress) => {
          console.log(`📊 Upload progress for file ${index + 1}: ${progress.percentage}%`);
          setUploadProgress(prev => ({
            ...prev,
            [`media_${index}`]: progress.percentage
          }));
        }
      );

      // Check if all uploads succeeded
      const failedUploads = uploadResults.filter(result => !result.success);
      if (failedUploads.length > 0) {
        console.error('❌ Some files failed to upload:', failedUploads);
        throw new Error('Some files failed to upload');
      }

      // Extract file URLs
      const uploadedUrls = uploadResults
        .filter(result => result.success && result.fileUrl)
        .map(result => result.fileUrl!);

      console.log('✅ All files uploaded successfully:', uploadedUrls);
      return uploadedUrls;

    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const handleSendEmail = async () => {
    if (!athleteId || !isGmailConnected) return;
    if (!subject.trim() || !emailBody.trim()) {
      toast.warning('Please fill in both subject and message.');
      return;
    }

    // Validate Cc/Bcc addresses up front so a malformed token can't be sent
    // (the API would otherwise silently bounce it).
    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const badRecipient = [...ccEmails.split(','), ...bccEmails.split(',')]
      .map(e => e.trim())
      .filter(e => e.length > 0)
      .find(e => !EMAIL_RE.test(e));
    if (badRecipient) {
      toast.error(`"${badRecipient}" is not a valid email address. Check your Cc/Bcc.`);
      return;
    }

    // P2.5 — pre-flight is now silent-strip + passive signal. Previously
    // this hard-blocked send with a banner; user feedback was it's annoying.
    // New behavior: scan for unresolved {{tags}}, replace them with empty
    // strings in the final send (so coaches see a clean blank instead of
    // literal "{{athlete.gpa}}"), and fire analytics so we can measure
    // which fields are most often missing without taxing the UX. The
    // compose-time passive signal lives in the bottom of the editor (see
    // the JSX below) — a one-liner pointing users at /profile.
    const previewedSubject = resolveVariables(subject.trim());
    const previewedBody = resolveVariables(emailBody.trim());
    const unresolvedTagSet = new Set<string>();
    const tagRe = /\{\{\s*([^}\s]+?)\s*\}\}/g;
    let m: RegExpExecArray | null;
    while ((m = tagRe.exec(`${previewedSubject}\n${previewedBody}`)) !== null) {
      unresolvedTagSet.add(m[1]);
    }
    if (unresolvedTagSet.size > 0) {
      try {
        const analytics = (await import('../services/analytics')).default;
        analytics.track('email_send_missing_tags', {
          surface: 'email_coach',
          missing_fields: Array.from(unresolvedTagSet),
        });
      } catch {}
    }

    // Silent-strip: replace any remaining {{...}} with empty string so the
    // recipient sees blanks instead of curly-brace literals. resolveVariables
    // already fills everything it can; this catches whatever it couldn't.
    const cleanSubject = previewedSubject.replace(tagRe, '');
    const cleanBody = previewedBody.replace(tagRe, '');

    setIsSending(true);

    try {
      let attachmentObjects: { filename: string; url: string; content_type: string; size?: number }[] = [];

      // Upload attachments if any
      if (attachedMedia.length > 0) {
        setIsUploading(true);
        const uploadedUrls = await uploadMediaFiles();

        attachmentObjects = attachedMedia.map((media, index) => ({
          filename: media.file.name,
          url: uploadedUrls[index],
          content_type: media.file.type,
          size: media.file.size
        }));

        setIsUploading(false);
      }

      // Use the cleaned subject/body from the pre-send pass above. Any
      // unresolved {{tags}} have been stripped to empty strings rather than
      // sent literal — coaches see a blank line in the signature instead of
      // "{{athlete.gpa}}". resolvedSubject/resolvedBody are kept as the
      // names of the variables that flow into the request below for
      // backwards-readability.
      const resolvedSubject = cleanSubject;
      const resolvedBody = cleanBody;

      // Parse CC/BCC emails
      const parsedCc = ccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);
      const parsedBcc = bccEmails.split(',').map(e => e.trim()).filter(e => e.length > 0);

      await integrationsAPI.sendGmail(athleteId, {
        recipients: [{
          email: coach.email,
          coach_id: coach.coach_id,
          school_id: coach.school_id
        }],
        subject: resolvedSubject,
        body: resolvedBody,
        reply_to: undefined,
        display_name: athleteProfile ? `${athleteProfile.firstName || ''} ${athleteProfile.lastName || ''}`.trim() : 'Athlete',
        cc_emails: parsedCc.length > 0 ? parsedCc : undefined,
        bcc_emails: parsedBcc.length > 0 ? parsedBcc : undefined,
        attachments: attachmentObjects.length > 0 ? attachmentObjects : undefined
      });

      toast.success(`Email sent to ${coach.name}.`);
      handleClose();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(gmailSendErrorMessage(error));
    } finally {
      setIsSending(false);
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  // Route this composer's own X/Cancel through the Modal's guarded close so the
  // "Discard changes?" prompt fires from them too (not just backdrop/Escape).
  const composerModalRef = useRef<ModalHandle>(null);
  const handleClose = () => {
    setSubject('');
    setEmailBody('');
    setCcEmails('');
    setBccEmails('');
    setShowCcBcc(false);
    setAttachedMedia([]);
    setUploadProgress({});
    setIsCheckingConnection(true);
    setIsGmailConnected(false);
    setAthleteId(null);
    setAthleteProfile(null);
    setPreviewMode(false);
    hasSeededTemplateRef.current = false;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        ref={composerModalRef}
        inline={embedded}
        isDirty={subject.trim().length > 0 || emailBody.trim().length > 0}
        disableClose={isSending || youtubePickerVisible || templatePickerVisible}
        ariaLabel="Email Coach"
        zIndex={1001}
        contentStyle={{
          backgroundColor: 'white',
          borderRadius: '12px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '700px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
          {/* Header */}
          <div style={{
            backgroundColor: '#151133',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
              disabled={isSending}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: 'white'
            }}>
              Email Coach
            </h2>
            <div style={{ width: '40px' }} />
          </div>

          {/* Content */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#F9FAFB',
            padding: '24px'
          }}>
            {/* §0.4: warn + reconnect if this athlete's Gmail connection died */}
            <IntegrationHealthBanner
              athleteId={athleteId}
              token={sessionToken}
              onReconnected={checkGmailConnection}
            />
            {isCheckingConnection ? (
              /* Loading State */
              <div style={{
                backgroundColor: 'white',
                padding: '48px 32px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}>
                  <Mail size={48} color="#EA4335" />
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 12px'
                }}>
                  Initializing Email Composer...
                </h3>
              </div>
            ) : !isGmailConnected ? (
              /* Gmail Not Connected */
              <div style={{
                backgroundColor: 'white',
                padding: '48px 32px',
                borderRadius: '12px',
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#FEF2F2',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <Mail size={48} color="#EA4335" />
                </div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: '0 0 12px'
                }}>
                  Gmail Account Not Connected
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  lineHeight: '1.5',
                  margin: '0 0 16px'
                }}>
                  To send emails to coaches, you need to connect your Gmail account first.
                </p>
                <p style={{
                  fontSize: '14px',
                  color: '#F9550D',
                  lineHeight: '1.5',
                  margin: 0
                }}>
                  Go to Messages <ChevronRight size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" /> Settings to connect your Gmail account.
                </p>
              </div>
            ) : (
              /* Email Composer */
              <>
                {/* Coach Info */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#FEF2F2',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <Mail size={24} color="#EA4335" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '4px'
                    }}>
                      {coach.name}
                    </div>
                    {coach.title && (
                      <div style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        marginBottom: '2px'
                      }}>
                        {coach.title}
                      </div>
                    )}
                    {coach.school && (
                      <div style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        marginBottom: '4px'
                      }}>
                        {coach.school}
                      </div>
                    )}
                    <div style={{
                      fontSize: '12px',
                      color: '#9CA3AF'
                    }}>
                      {coach.email}
                    </div>
                  </div>
                </div>

                {/* Email Form */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                }}>
                  {/* Compose / Preview Toggle */}
                  <div style={{
                    display: 'flex',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '8px',
                    padding: '3px',
                    marginBottom: '20px',
                    gap: '4px'
                  }}>
                    <button
                      onClick={() => setPreviewMode(false)}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        backgroundColor: !previewMode ? '#151133' : 'transparent',
                        color: !previewMode ? 'white' : '#6B7280',
                        transition: 'all 0.2s'
                      }}
                    >
                      <Edit3 size={14} style={{ marginRight: 6 }} /> Compose
                    </button>
                    <button
                      onClick={() => setPreviewMode(true)}
                      disabled={!subject.trim() && !emailBody.trim()}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '8px 16px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: (!subject.trim() && !emailBody.trim()) ? 'not-allowed' : 'pointer',
                        backgroundColor: previewMode ? '#151133' : 'transparent',
                        color: previewMode ? 'white' : '#6B7280',
                        opacity: (!subject.trim() && !emailBody.trim()) ? 0.4 : 1,
                        transition: 'all 0.2s'
                      }}
                    >
                      <Eye size={14} style={{ marginRight: 6 }} /> Preview
                    </button>
                  </div>

                  {previewMode ? (
                    /* Preview Mode */
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Subject</div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1F2937',
                          padding: '12px 16px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB'
                        }}>
                          {resolveVariables(subject).replace(/\{\{\s*[^}]*?\s*\}\}/g, '') || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No subject</span>}
                        </div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '4px', fontWeight: '500' }}>Message</div>
                        <div style={{
                          fontSize: '14px',
                          color: '#1F2937',
                          padding: '16px',
                          backgroundColor: '#F9FAFB',
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.6',
                          minHeight: '200px'
                        }}>
                          {resolveVariables(emailBody).replace(/\{\{\s*[^}]*?\s*\}\}/g, '') || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No message</span>}
                        </div>
                      </div>
                      {attachedMedia.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px', fontWeight: '500' }}>Attachments ({attachedMedia.length})</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {attachedMedia.map((media, idx) => (
                              <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: '#F3F4F6',
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: '#374151'
                              }}>
                                <Paperclip size={12} />
                                {media.file.name || `Attachment ${idx + 1}`}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Compose Mode */
                    <>
                    {/* Template picker surfaced FIRST — starting from a template is
                        the intended path, and it used to be buried below the message
                        field so users hit Send without seeing it (KGH-785). */}
                    <button
                      onClick={() => setTemplatePickerVisible(true)}
                      disabled={isSending}
                      style={{
                        width: '100%',
                        marginBottom: '20px',
                        padding: '12px 16px',
                        backgroundColor: '#FFF7ED',
                        border: '2px solid #f9550d',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#f9550d',
                        opacity: isSending ? 0.5 : 1,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFEDD5')}
                      onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFF7ED')}
                    >
                      <File size={18} />
                      Start from a Template
                    </button>
                  {/* CC/BCC Toggle */}
                  <div style={{ marginBottom: '16px' }}>
                    {!showCcBcc ? (
                      <button
                        onClick={() => setShowCcBcc(true)}
                        disabled={isSending}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#6B7280',
                          cursor: 'pointer',
                          padding: '4px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#F9550D')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#6B7280')}
                      >
                        + Add CC / BCC
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div>
                          <label htmlFor="email-coach-cc" style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                            marginBottom: '4px',
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.5px'
                          }}>
                            CC
                          </label>
                          <TextInput
                            id="email-coach-cc"
                            type="text"
                            value={ccEmails}
                            onChange={(e) => setCcEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            disabled={isSending}
                            style={{
                              padding: '10px 14px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '13px',
                            }}
                          />
                        </div>
                        <div>
                          <label htmlFor="email-coach-bcc" style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#6B7280',
                            marginBottom: '4px',
                            textTransform: 'uppercase' as const,
                            letterSpacing: '0.5px'
                          }}>
                            BCC
                          </label>
                          <TextInput
                            id="email-coach-bcc"
                            type="text"
                            value={bccEmails}
                            onChange={(e) => setBccEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            disabled={isSending}
                            style={{
                              padding: '10px 14px',
                              border: '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '13px',
                            }}
                          />
                        </div>
                        <button
                          onClick={() => { setShowCcBcc(false); setCcEmails(''); setBccEmails(''); }}
                          style={{
                            alignSelf: 'flex-start',
                            background: 'none',
                            border: 'none',
                            fontSize: '12px',
                            color: '#9CA3AF',
                            cursor: 'pointer',
                            padding: '2px 0'
                          }}
                        >
                          Remove CC/BCC
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Subject */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="email-coach-subject" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000000',
                      marginBottom: '8px'
                    }}>
                      Subject
                    </label>
                    <TextInput
                      id="email-coach-subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Email subject"
                      disabled={isSending}
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="email-coach-body" style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#000000',
                      marginBottom: '8px'
                    }}>
                      Message
                    </label>
                    <TextArea
                      id="email-coach-body"
                      ref={emailBodyRef}
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      onSelect={(e) => {
                        const target = e.target as HTMLTextAreaElement;
                        setCursorPosition(target.selectionStart);
                      }}
                      placeholder="Write your message..."
                      disabled={isSending}
                      style={{
                        height: '200px',
                        padding: '12px 16px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />

                    {/* Quick Tags */}
                    <QuickTags
                      tags={tags}
                      onInsertTag={insertQuickTag}
                      disabled={isSending}
                    />

                    {/* Hint: the Profile Share Link tag is only offered when
                        the athlete has profile sharing turned on. */}
                    {!profileShareUrl && (
                      <p style={{
                        margin: '8px 0 0',
                        fontSize: '12px',
                        color: '#6B7280',
                        lineHeight: 1.4,
                      }}>
                        Tip: Turn on profile sharing in your profile's Share settings to add a
                        <strong> Profile Share Link</strong> tag to your emails.
                      </p>
                    )}

                    {/* YouTube Videos Button */}
                    <button
                      onClick={() => setYoutubePickerVisible(true)}
                      disabled={isSending}
                      style={{
                        width: '100%',
                        marginTop: '16px',
                        padding: '16px 20px',
                        backgroundColor: '#FFF5F5',
                        border: '2px solid #FF0000',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: isSending ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#FF0000',
                        opacity: isSending ? 0.5 : 1,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFE5E5')}
                      onMouseLeave={(e) => !isSending && (e.currentTarget.style.backgroundColor = '#FFF5F5')}
                    >
                      <PlayCircle size={20} />
                      YouTube Videos
                    </button>
                  </div>

                  {/* Attachments */}
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSending || isUploading || attachedMedia.length >= 4}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: '#F8FAFC',
                        border: '2px dashed #F9550D',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#F9550D',
                        marginBottom: '15px'
                      }}
                    >
                      <Paperclip size={16} />
                      {isUploading ? 'Uploading...' : 'Add File'}
                    </button>

                    {/* Media Preview */}
                    {attachedMedia.length > 0 && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '20px',
                        overflowX: 'auto',
                        paddingBottom: '4px'
                      }}>
                        {attachedMedia.map((media, index) => (
                          <div key={index} style={{
                            position: 'relative',
                            flexShrink: 0
                          }}>
                            {media.type === 'document' ? (
                              <div style={{
                                width: '100px',
                                height: '100px',
                                backgroundColor: '#F3F4F6',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px'
                              }}>
                                <File size={36} color="#6B7280" />
                                <div style={{
                                  fontSize: '10px',
                                  color: '#374151',
                                  textAlign: 'center',
                                  marginTop: '4px',
                                  wordBreak: 'break-word'
                                }}>
                                  {media.file.name}
                                </div>
                              </div>
                            ) : (
                              <img
                                src={media.preview}
                                alt="preview"
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  backgroundColor: '#F3F4F6'
                                }}
                              />
                            )}

                            {/* Video icon overlay */}
                            {media.type === 'video' && (
                              <div style={{
                                position: 'absolute',
                                bottom: '8px',
                                left: '8px',
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '20px',
                                padding: '4px'
                              }}>
                                <PlayCircle size={24} color="white" />
                              </div>
                            )}

                            {/* Upload progress */}
                            {isUploading && uploadProgress[`media_${index}`] !== undefined && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                borderRadius: '8px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}>
                                <div>{Math.round(uploadProgress[`media_${index}`] || 0)}%</div>
                                <div style={{ fontSize: '10px', marginTop: '2px', opacity: 0.8 }}>
                                  {uploadProgress[`media_${index}`] < 50 ? 'Compressing...' : 'Uploading...'}
                                </div>
                              </div>
                            )}

                            {/* Remove button */}
                            <button
                              onClick={() => removeMediaFile(index)}
                              disabled={isSending || isUploading}
                              style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                                border: '1px solid white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              <X size={14} color="white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Attachment Guidelines */}
                    <div style={{
                      fontSize: '12px',
                      color: '#6B7280',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      Maximum file size: 10MB.<br />
                      Supported formats: PDF, DOC, images, and more.
                    </div>
                  </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Action Buttons - Only show when Gmail is connected */}
          {isGmailConnected && (
            <div style={{
              padding: '20px 24px',
              borderTop: '1px solid #E5E7EB',
              backgroundColor: 'white',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => (composerModalRef.current ? composerModalRef.current.requestClose() : handleClose())}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: '2px solid #D1D5DB',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={!subject.trim() || !emailBody.trim() || isSending}
                style={{
                  flex: 1,
                  padding: '14px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#F9550D',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: (!subject.trim() || !emailBody.trim() || isSending) ? 0.5 : 1
                }}
              >
                <Mail size={16} />
                {isSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          )}
        </Modal>

      {/* YouTube Video Picker Modal */}
      <YouTubeVideoPickerModal
        isOpen={youtubePickerVisible}
        onClose={() => setYoutubePickerVisible(false)}
        onSelectVideos={handleYouTubeVideosSelected}
        title="Select Videos"
      />

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={templatePickerVisible}
        onClose={() => setTemplatePickerVisible(false)}
        onApply={(template) => {
          if (template.subject) setSubject(template.subject);
          setEmailBody(template.body);
        }}
      />
    </>
  );
};

export default EmailCoachModal;
